<template>
    <div class="login-container">
        <div class="login-box">
            <div class="login-header">
                <h2>登录</h2>
                <p>小鱼智能控制平台</p>
            </div>

            <!-- Password Login Form -->
            <el-form v-if="!qrcodeMode" ref="loginForm" :model="loginForm" :rules="loginRules" class="login-form" label-width="0">
                <el-form-item prop="account">
                    <el-input autocomplete="email" v-model="loginForm.account" placeholder="请输入用户名或邮箱" prefix-icon="el-icon-user"
                              @keyup.enter.native="handleLogin"/>
                </el-form-item>

                <el-form-item prop="password">
                    <el-input autocomplete="password" v-model="loginForm.password" type="password" placeholder="请输入密码"
                              prefix-icon="el-icon-lock"
                              show-password @keyup.enter.native="handleLogin"/>
                </el-form-item>

                <el-form-item>
                    <el-button type="primary" :loading="loading" class="login-btn" @click="handleLogin">
                        登录
                    </el-button>
                </el-form-item>

                <el-form-item>
                    <el-button type="success" class="wechat-login-btn" @click="showQRCodeLogin">
                        <i class="el-icon-mobile-phone"></i> 微信扫码登录
                    </el-button>
                </el-form-item>

                <div class="login-links">
                    <div class="forgot-password">
                        <router-link to="/forgot-password">忘记密码？</router-link>
                    </div>
                    <div class="register-link">
                        还没有账号？
                        <router-link to="/register">立即注册</router-link>
                    </div>
                </div>
            </el-form>

            <!-- QR Code Login Section -->
            <div v-else class="qrcode-section">
                <div class="qrcode-header">
                    <h3>微信扫码登录</h3>
                    <p>请使用微信扫描二维码</p>
                </div>

                <!-- Loading State -->
                <div v-if="qrcodeLoading" class="qrcode-loading">
                    <i class="el-icon-loading"></i>
                    <p>正在生成二维码...</p>
                </div>

                <!-- QR Code Display -->
                <div v-else-if="qrcodeImage" class="qrcode-display">
                    <!-- 二维码容器，过期时可点击刷新 -->
                    <div 
                        class="qrcode-wrapper" 
                        :class="{ 'expired': timeRemaining <= 0 }"
                        @click="timeRemaining <= 0 ? requestQRCode() : null"
                    >
                        <img :src="qrcodeImage" alt="WeChat QR Code" class="qrcode-image" />
                        
                        <!-- 过期遮罩层 -->
                        <div v-if="timeRemaining <= 0" class="qrcode-expired-overlay">
                            <i class="el-icon-refresh-right"></i>
                            <p>二维码已过期</p>
                            <span>点击刷新</span>
                        </div>
                    </div>
                    
                    <!-- Waiting Status -->
                    <div v-if="timeRemaining > 0" class="qrcode-status">
                        <i class="el-icon-info"></i>
                        <span>等待扫码中...</span>
                    </div>

                    <!-- Countdown Timer -->
                    <div v-if="timeRemaining > 0" class="qrcode-timer">
                        <i class="el-icon-time"></i>
                        <span>{{ formatTime(timeRemaining) }} 后过期</span>
                    </div>
                </div>

                <!-- Error State -->
                <div v-else-if="qrcodeError" class="qrcode-error">
                    <i class="el-icon-error"></i>
                    <p>{{ qrcodeError }}</p>
                    <el-button type="primary" size="small" @click="requestQRCode">重试</el-button>
                </div>

                <!-- Return to Password Login -->
                <div class="qrcode-footer">
                    <el-button type="text" @click="returnToPasswordLogin">
                        <i class="el-icon-back"></i> 返回密码登录
                    </el-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { generateQRCode, checkQRCodeStatus } from '@/api/login'

export default {
    name: "UserLogin",
    data() {
        return {
            loading: false,
            loginForm: {
                account: "",
                password: "",
            },
            loginRules: {
                account: [
                    {required: true, message: "请输入用户名或邮箱", trigger: "blur"},
                ],
                password: [{required: true, message: "请输入密码", trigger: "blur"}],
            },
            // 二维码登录状态
            qrcodeMode: false,
            qrcodeLoading: false,
            qrcodeImage: null,
            qrcodeError: null,
            sceneValue: null,
            pollingInterval: null,
            expiresAt: null,
            timeRemaining: 0,
            countdownInterval: null,
        };
    },
    beforeDestroy() {
        // 组件销毁时清理定时器
        this.stopPolling();
        this.stopCountdown();
    },
    methods: {
        async handleLogin() {

            this.loading = true;

            this.$store.dispatch("Login", this.loginForm).then(() => {
                // 登录成功后获取用户信息
                this.$store.dispatch("GetInfo").then(() => {
                    this.$message.success("登录成功！");
                    // 判断用户ID是否为1，如果是则跳转到admin页面
                    const userId = this.$store.getters.id;
                    if (userId === 1) {
                        this.$router.push({path: "/admin"}).catch(() => {
                        });
                    } else {
                        this.$router.push({path: this.redirect || "/"}).catch(() => {
                        });
                    }
                }).catch(() => {
                    this.loading = false;
                });
            }).catch(() => {
                this.loading = false;
            });
        },

        // 切换到二维码登录模式
        showQRCodeLogin() {
            this.qrcodeMode = true;
            this.requestQRCode();
        },

        // 从后端请求二维码
        async requestQRCode() {
            this.qrcodeLoading = true;
            this.qrcodeError = null;
            this.qrcodeImage = null;
            this.sceneValue = null;

            try {
                const response = await generateQRCode();
                
                // 检查响应是否成功
                if (response.code === 200 && response.data) {
                    // 存储场景值
                    this.sceneValue = response.data.sceneValue;
                    
                    // 显示二维码图片（base64编码）
                    this.qrcodeImage = `data:image/jpeg;base64,${response.data.qrcodeImage}`;
                    
                    // 启动倒计时
                    const expiresIn = response.data.expiresIn || 300;
                    this.startCountdown(expiresIn);
                    
                    // 开始轮询认证状态
                    this.startPolling();
                } else {
                    // 处理意外的响应格式
                    this.qrcodeError = response.message || '生成二维码失败，请重试';
                }
            } catch (error) {
                // 处理API错误
                console.error('QR code generation error:', error);
                
                // 显示用户友好的错误消息
                if (error.response && error.response.data && error.response.data.message) {
                    this.qrcodeError = error.response.data.message;
                } else if (error.message) {
                    this.qrcodeError = error.message;
                } else {
                    this.qrcodeError = '生成二维码失败，请检查网络连接后重试';
                }
            } finally {
                this.qrcodeLoading = false;
            }
        },

        // 开始轮询认证状态
        startPolling() {
            if (this.pollingInterval) {
                return;
            }

            this.pollingInterval = setInterval(() => {
                this.checkAuthStatus();
            }, 1000); // 每1秒轮询一次
        },

        // 停止轮询
        stopPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }
        },

        // 检查认证状态
        async checkAuthStatus() {
            // 如果没有场景值则不轮询
            if (!this.sceneValue) {
                return;
            }

            try {
                const response = await checkQRCodeStatus(this.sceneValue);
                
                // 处理成功响应
                if (response.code === 200 && response.data) {
                    const status = response.data.status;
                    
                    if (status === 'success') {
                        // 认证成功完成
                        this.handleAuthSuccess(response.data);
                    } else if (status === 'pending') {
                        // 仍在等待用户扫码和认证
                        // 继续轮询（不做任何操作，定时器会再次调用）
                    }
                } else if (response.code === 400 && response.data && response.data.status === 'expired') {
                    // 二维码已过期
                    this.handleExpired();
                } else {
                    // 意外响应，记录日志但继续轮询
                    console.warn('Unexpected status response:', response);
                }
            } catch (error) {
                // 优雅地处理网络错误
                console.error('Status polling error:', error);
                
                // 检查是否为过期状态错误（400状态码且包含过期消息）
                if (error.response && error.response.status === 400) {
                    const errorData = error.response.data;
                    if (errorData && errorData.data && errorData.data.status === 'expired') {
                        this.handleExpired();
                        return;
                    }
                }
                
                // 对于其他网络错误，继续轮询
                // 不要因为临时网络错误而停止轮询
                // 轮询会在下一个间隔重试
            }
        },

        // 处理认证成功
        handleAuthSuccess(data) {
            this.stopPolling();
            this.stopCountdown();

            // 从响应中提取令牌和用户信息
            const token = data.token;
            const userInfo = data.userInfo;

            if (!token || !userInfo) {
                this.$message.error('登录响应数据不完整');
                return;
            }

            // 使用现有的auth工具将JWT令牌存储到cookies中
            this.$store.commit('SET_TOKEN', token);
            this.$store.commit('SET_ID', userInfo.id);
            this.$store.commit('SET_NAME', userInfo.username);
            this.$store.commit('SET_EMAIL', userInfo.email || '');

            // setToken已经可以从store action中导入
            // 但我们需要显式地将其设置到cookies中
            const { setToken } = require('@/utils/auth');
            setToken(token);

            // 显示成功消息
            this.$message.success('登录成功！');

            // 根据用户角色重定向
            // 管理员用户（ID = 1）跳转到管理员仪表板
            // 其他用户跳转到用户仪表板
            if (userInfo.id === 1) {
                this.$router.push({ path: '/admin' }).catch(() => {});
            } else {
                this.$router.push({ path: this.redirect || '/' }).catch(() => {});
            }
        },

        // 处理二维码过期
        handleExpired() {
            this.stopPolling();
            this.stopCountdown();
            this.timeRemaining = 0;
            
            // 可选：自动重新生成二维码
            // 目前用户可以手动点击刷新按钮
            // 如果需要自动重新生成，取消下面一行的注释：
            // this.requestQRCode();
        },

        // 返回密码登录模式
        returnToPasswordLogin() {
            this.stopPolling();
            this.stopCountdown();
            this.qrcodeMode = false;
            this.qrcodeImage = null;
            this.qrcodeError = null;
            this.sceneValue = null;
            this.timeRemaining = 0;
        },

        // 启动倒计时
        startCountdown(expiresIn) {
            this.timeRemaining = expiresIn;
            this.expiresAt = Date.now() + expiresIn * 1000;

            if (this.countdownInterval) {
                clearInterval(this.countdownInterval);
            }

            this.countdownInterval = setInterval(() => {
                const remaining = Math.floor((this.expiresAt - Date.now()) / 1000);
                this.timeRemaining = Math.max(0, remaining);

                if (this.timeRemaining <= 0) {
                    this.handleExpired();
                }
            }, 1000);
        },

        // 停止倒计时
        stopCountdown() {
            if (this.countdownInterval) {
                clearInterval(this.countdownInterval);
                this.countdownInterval = null;
            }
        },

        // 格式化剩余时间（MM:SS）
        formatTime(seconds) {
            const minutes = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        },
    },
};
</script>

<style scoped>
.login-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #eac966 0%, #77d8e1 100%);
}

.login-box {
    width: 400px;
    padding: 40px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
}

.login-header {
    text-align: center;
    margin-bottom: 30px;
}

.login-header h2 {
    color: #333;
    margin-bottom: 10px;
    font-size: 28px;
}

.login-header p {
    color: #666;
    font-size: 14px;
}

.login-form {
    margin-top: 20px;
}

.login-btn {
    width: 100%;
    height: 45px;
    font-size: 16px;
    border-radius: 5px;
}

.wechat-login-btn {
    width: 100%;
    height: 45px;
    font-size: 16px;
    border-radius: 5px;
    background-color: #07c160;
    border-color: #07c160;
    color: white;
}

.wechat-login-btn:hover {
    background-color: #06ad56;
    border-color: #06ad56;
}

.login-links {
    margin-top: 20px;
}

.forgot-password {
    text-align: right;
    margin-bottom: 10px;
}

.forgot-password a {
    color: #409eff;
    text-decoration: none;
    font-size: 14px;
}

.forgot-password a:hover {
    text-decoration: underline;
}

.register-link {
    text-align: center;
    color: #666;
    font-size: 14px;
}

.register-link a {
    color: #409eff;
    text-decoration: none;
}

.register-link a:hover {
    text-decoration: underline;
}

/* QR Code Section Styles */
.qrcode-section {
    margin-top: 20px;
}

.qrcode-header {
    text-align: center;
    margin-bottom: 30px;
}

.qrcode-header h3 {
    color: #333;
    margin-bottom: 10px;
    font-size: 22px;
}

.qrcode-header p {
    color: #666;
    font-size: 14px;
}

.qrcode-loading {
    text-align: center;
    padding: 60px 0;
}

.qrcode-loading i {
    font-size: 48px;
    color: #409eff;
    margin-bottom: 20px;
}

.qrcode-loading p {
    color: #666;
    font-size: 16px;
}

.qrcode-display {
    text-align: center;
}

.qrcode-wrapper {
    position: relative;
    width: 240px;
    height: 240px;
    margin: 0 auto 20px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 10px;
    background: white;
    transition: all 0.3s ease;
}

.qrcode-wrapper.expired {
    cursor: pointer;
}

.qrcode-wrapper.expired:hover {
    border-color: #409eff;
    box-shadow: 0 0 10px rgba(64, 158, 255, 0.3);
}

.qrcode-image {
    width: 100%;
    height: 100%;
    display: block;
    transition: filter 0.3s ease;
}

.qrcode-wrapper.expired .qrcode-image {
    filter: grayscale(100%) brightness(0.8);
}

.qrcode-expired-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.85);
    border-radius: 8px;
    transition: all 0.3s ease;
}

.qrcode-wrapper.expired:hover .qrcode-expired-overlay {
    background: rgba(64, 158, 255, 0.1);
}

.qrcode-expired-overlay i {
    font-size: 64px;
    color: #909399;
    margin-bottom: 10px;
    animation: rotate 2s linear infinite;
}

.qrcode-wrapper.expired:hover .qrcode-expired-overlay i {
    color: #409eff;
}

.qrcode-expired-overlay p {
    font-size: 16px;
    color: #606266;
    margin: 0 0 5px 0;
    font-weight: 500;
}

.qrcode-expired-overlay span {
    font-size: 14px;
    color: #909399;
}

.qrcode-wrapper.expired:hover .qrcode-expired-overlay span {
    color: #409eff;
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.qrcode-status {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 15px;
    color: #409eff;
    font-size: 14px;
}

.qrcode-status i {
    margin-right: 8px;
    font-size: 16px;
}

.qrcode-timer {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    color: #666;
    font-size: 14px;
}

.qrcode-timer i {
    margin-right: 8px;
    font-size: 16px;
}

.qrcode-error {
    text-align: center;
    padding: 40px 20px;
}

.qrcode-error i {
    font-size: 48px;
    color: #f56c6c;
    margin-bottom: 20px;
}

.qrcode-error p {
    color: #666;
    font-size: 14px;
    margin-bottom: 20px;
}

.qrcode-footer {
    text-align: center;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #e0e0e0;
}

.qrcode-footer .el-button {
    color: #409eff;
    font-size: 14px;
}

.qrcode-footer .el-button:hover {
    color: #66b1ff;
}

@media (max-width: 480px) {
    .login-box {
        width: 90%;
        padding: 30px 20px;
    }

    .qrcode-image {
        width: 200px;
        height: 200px;
    }
}
</style>
